import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";

import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { closeModal, SweetAlertShow, LoadingDiv } from "../master/Master";

import {
  CreateHostel,
  DeleteHostelData,
  GetHostelList,
  StatusChange,
  UpdateHostel,
  addStafftoHostel,
} from "../../api/HostelService";

import usePrivateLink from "../config/usePrivateLink";
import StudentsModal from "./StudentsModal";
import { StaffList } from "../../api/Staff";

export default function Main() {
  const hasAccess = usePrivateLink();
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [selectedHostel, setSelectedHostel] = useState(null);
  const [selectedStaffIds, setSelectedStaffIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hostels, setHostels] = useState({});
  const [page, setPage] = useState(1);
  const [modalData, setModalData] = useState(null);
  const [studentType, setStudentType] = useState("all");

  useEffect(() => {
    const userPermissions = localStorage.getItem("user_permissions");
    const parsedPermissions = userPermissions
      ? JSON.parse(userPermissions)
      : null;
    const roleId = parsedPermissions?.id;
    setIsSuperAdmin(Number(roleId) === 1);
  }, []);

  /* =========================================================
     FETCH HOSTELS
  ========================================================= */
  const getHostelList = useCallback(async () => {
    setLoading(true);

    try {
      const response = await GetHostelList(`&page=${page}`);

      if (response?.data?.status) {
        setHostels(response?.data?.data || {});
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to fetch hostel data.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message ??
          "Something went wrong.",
      );
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    getHostelList();
  }, [getHostelList]);

  const getStaffList = async () => {
    try {
      setLoading(true);

      const response = await StaffList();

      if (response?.data?.status) {
        const data = response?.data?.data;

        setStaffList(
          Array.isArray(data)
            ? data
            : Array.isArray(data?.data)
              ? data.data
              : [],
        );
      } else {
        setStaffList([]);

        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to fetch staff.",
        );
      }
    } catch (error) {
      setStaffList([]);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message ??
          "Unable to fetch staff.",
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     DASHBOARD COUNTS
  ========================================================= */
  const statistics = useMemo(() => {
    const data = Array.isArray(hostels?.data) ? hostels.data : [];

    return data.reduce(
      (acc, hostel) => {
        acc.students += Number(hostel?.students_count || 0);
        acc.activeStudents += Number(hostel?.active_students_count || 0);
        acc.inactiveStudents += Number(hostel?.inactive_students_count || 0);
        acc.rooms += Number(hostel?.rooms_count || 0);
        acc.beds += Number(hostel?.beds_count || 0);

        return acc;
      },
      {
        students: 0,
        activeStudents: 0,
        inactiveStudents: 0,
        rooms: 0,
        beds: 0,
      },
    );
  }, [hostels]);

  /* =========================================================
     CREATE HOSTEL
  ========================================================= */
  const createHostel = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData(e.target);
      const response = await CreateHostel(formData);

      if (response?.data?.status) {
        e.target.reset();
        closeModal("addNewHostel");

        SweetAlertShow("success", "Successfully", response?.data?.message);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
      getHostelList();
    }
  };

  /* =========================================================
     STATUS CHANGE
  ========================================================= */
  const handleStatusChange = async (id) => {
    setLoading(true);

    try {
      const response = await StatusChange(id);

      if (response?.data?.status) {
        SweetAlertShow("success", "Successfully", response?.data?.message);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
      getHostelList();
    }
  };

  /* =========================================================
     UPDATE HOSTEL
  ========================================================= */
  const updateHostel = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData(e.target);
      const response = await UpdateHostel(formData);

      if (response?.data?.status) {
        closeModal("editHostel");

        SweetAlertShow("success", "Successfully", response?.data?.message);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
      getHostelList();
    }
  };

  /* =========================================================
     DELETE HOSTEL
  ========================================================= */
  const deleteHostel = async (id) => {
    setLoading(true);

    try {
      const response = await DeleteHostelData(id);

      if (response?.data?.status) {
        SweetAlertShow("success", "Successfully", response?.data?.message);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
      getHostelList();
    }
  };

  /* =========================================================
     DELETE CONFIRMATION
  ========================================================= */
  const confirmDelete = (hostel) => {
    Swal.fire({
      title: "Delete Hostel?",
      html: `
        <div class="text-muted">
          You are about to permanently delete
          <strong>${hostel?.name || "this hostel"}</strong>.
          <br/>
          This action cannot be undone.
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d63939",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      customClass: {
        confirmButton: "btn btn-danger px-4",
        cancelButton: "btn btn-secondary px-4",
      },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        deleteHostel(hostel?.id);
      }
    });
  };

  /* =========================================================
     PAGINATION
  ========================================================= */
  const handlePageChange = (btn, index) => {
    if (!btn?.url) return;

    if (index === 0) {
      setPage(Math.max(1, Number(hostels?.current_page || 1) - 1));
      return;
    }

    if (index === hostels?.links?.length - 1) {
      setPage(Number(hostels?.current_page || 1) + 1);
      return;
    }

    const selectedPage = Number(btn?.label);

    if (!Number.isNaN(selectedPage)) {
      setPage(selectedPage);
    }
  };

  /* =========================================================
     STUDENT MODAL
  ========================================================= */
  const openStudentModal = (type) => {
    setStudentType(type);
  };

  const assignStaffToHostel = async (e) => {
    e.preventDefault();

    if (!selectedHostel?.id) {
      SweetAlertShow("info", "Warning", "Please select a hostel.");
      return;
    }

    if (!selectedStaffIds.length) {
      SweetAlertShow(
        "info",
        "Warning",
        "Please select at least one staff member.",
      );
      return;
    }

    const data = {
      hostel_id: selectedHostel.id,
      staff_ids: selectedStaffIds,
    };

    console.log(data);

    setLoading(true);

    try {
      const response = await addStafftoHostel(data);

      if (response?.data?.status) {
        SweetAlertShow(
          "success",
          "Successfully",
          response?.data?.message || "Staff assigned to hostel successfully.",
        );

        e.target.reset();

        setSelectedStaffIds([]);
        setSelectedHostel(null);

        closeModal("assignStaffToHostel");

        getHostelList();
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to assign staff.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message ??
          "Something went wrong.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <LoadingDiv />}
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-xxl flex-grow-1 container-p-y">
                {/* =================================================
                    PAGE HEADER
                ================================================= */}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
                  <div>
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <div
                        className="avatar avatar-sm"
                        style={{
                          width: 38,
                          height: 38,
                        }}
                      >
                        <span className="avatar-initial rounded bg-label-primary">
                          <i className="ri-building-4-line ri-20px" />
                        </span>
                      </div>

                      <h3 className="mb-0 fw-bold text-primary">
                        Hostel Management
                      </h3>
                    </div>

                    <p className="text-muted mb-0 ms-md-5">
                      Manage hostels, students, rooms and accommodation details.
                    </p>
                  </div>

                  {hasAccess("/hostel/add") && (
                    <button
                      type="button"
                      className="btn btn-primary px-4"
                      data-bs-toggle="modal"
                      data-bs-target="#addNewHostel"
                    >
                      <i className="ri-add-line me-1" />
                      Add Hostel
                    </button>
                  )}
                </div>
                {/* =================================================
                    STATISTICS
                ================================================= */}
                <div className="row g-4 mb-4">
                  {/* Total Hostels */}
                  <div className="col-xl-2 col-md-4 col-sm-6">
                    <div className="hostel-stat-card stat-warning h-100">
                      <div className="stat-icon">
                        <i className="ri-building-4-line" />
                      </div>

                      <div className="stat-content">
                        <span>Total Hostels</span>
                        <h3>{hostels?.total || 0}</h3>
                      </div>
                    </div>
                  </div>

                  {/* Total Students */}
                  <div className="col-xl-2 col-md-4 col-sm-6">
                    <div
                      className="hostel-stat-card stat-primary h-100 clickable"
                      onClick={() => openStudentModal("all")}
                      data-bs-toggle="modal"
                      data-bs-target="#StudentData"
                      role="button"
                      tabIndex={0}
                    >
                      <div className="stat-icon">
                        <i className="ri-group-line" />
                      </div>

                      <div className="stat-content">
                        <span>Total Students</span>
                        <h3>{statistics.students}</h3>
                      </div>

                      <i className="ri-arrow-right-up-line stat-arrow" />
                    </div>
                  </div>

                  {/* Active Students */}
                  <div className="col-xl-2 col-md-4 col-sm-6">
                    <div
                      className="hostel-stat-card stat-success h-100 clickable"
                      onClick={() => openStudentModal("active")}
                      data-bs-toggle="modal"
                      data-bs-target="#StudentData"
                      role="button"
                      tabIndex={0}
                    >
                      <div className="stat-icon">
                        <i className="ri-user-follow-line" />
                      </div>

                      <div className="stat-content">
                        <span>Active Students</span>
                        <h3>{statistics.activeStudents}</h3>
                      </div>

                      <i className="ri-arrow-right-up-line stat-arrow" />
                    </div>
                  </div>

                  {/* Inactive Students */}
                  <div className="col-xl-2 col-md-4 col-sm-6">
                    <div
                      className="hostel-stat-card stat-danger h-100 clickable"
                      onClick={() => openStudentModal("inactive")}
                      data-bs-toggle="modal"
                      data-bs-target="#StudentData"
                      role="button"
                      tabIndex={0}
                    >
                      <div className="stat-icon">
                        <i className="ri-user-unfollow-line" />
                      </div>

                      <div className="stat-content">
                        <span>Inactive Students</span>
                        <h3>{statistics.inactiveStudents}</h3>
                      </div>

                      <i className="ri-arrow-right-up-line stat-arrow" />
                    </div>
                  </div>

                  {/* Rooms */}
                  <div className="col-xl-2 col-md-4 col-sm-6">
                    <div className="hostel-stat-card stat-info h-100">
                      <div className="stat-icon">
                        <i className="ri-hotel-line" />
                      </div>

                      <div className="stat-content">
                        <span>Total Rooms</span>
                        <h3>{statistics.rooms}</h3>
                      </div>
                    </div>
                  </div>

                  {/* Beds */}
                  <div className="col-xl-2 col-md-4 col-sm-6">
                    <div className="hostel-stat-card stat-purple h-100">
                      <div className="stat-icon">
                        <i className="ri-hotel-bed-line" />
                      </div>

                      <div className="stat-content">
                        <span>Total Beds</span>
                        <h3>{statistics.beds}</h3>
                      </div>
                    </div>
                  </div>
                </div>
                {/* =========================================================
    HOSTEL LIST
========================================================= */}
                <div className="card border-0 shadow-sm overflow-hidden hostel-management-card">
                  {/* Header */}
                  <div className="card-header border-bottom py-3 px-4">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                      <div>
                        <div className="d-flex align-items-center gap-2">
                          <div className="hostel-section-icon">
                            <i className="ri-building-4-line" />
                          </div>

                          <div>
                            <h4 className="mb-0 fw-bold">Hostels</h4>

                            <small className="text-muted">
                              All registered hostel facilities
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* =========================================================
      HOSTEL CARDS
  ========================================================= */}
                  <div className="hostel-grid-container">
                    {Array.isArray(hostels?.data) && hostels.data.length > 0 ? (
                      <div className="hostel-grid">
                        {hostels.data.map((hostel, index) => {
                          const isActive = String(hostel?.status) === "1";

                          return (
                            <div
                              className="hostel-card"
                              key={hostel?.id || index}
                            >
                              {/* =================================================
                  CARD HEADER
              ================================================= */}
                              <div className="hostel-card-header">
                                <div className="d-flex align-items-center gap-3 min-width-0">
                                  <div className="hostel-avatar">
                                    <i className="ri-building-4-line" />
                                  </div>

                                  <div className="hostel-card-title">
                                    <div className="d-flex align-items-center gap-2">
                                      <h5
                                        className="mb-0 fw-bold text-heading hostel-name"
                                        title={hostel?.name}
                                      >
                                        {hostel?.name || "Unnamed Hostel"}
                                      </h5>
                                    </div>

                                    <span className="hostel-number">
                                      Hostel #
                                      {Number(hostels?.from || 1) + index}
                                    </span>
                                  </div>
                                </div>

                                {/* Status */}
                                <div className="hostel-status-wrapper">
                                  {hasAccess("/hostel/status") ? (
                                    <div className="d-flex align-items-center gap-2">
                                      <div className="form-check form-switch m-0">
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          role="switch"
                                          checked={isActive}
                                          onChange={() =>
                                            handleStatusChange(hostel?.id)
                                          }
                                          aria-label={`Change status for ${hostel?.name}`}
                                        />
                                      </div>

                                      <span
                                        className={`status-badge ${
                                          isActive
                                            ? "status-active"
                                            : "status-inactive"
                                        }`}
                                      >
                                        <span className="status-dot" />
                                        {isActive ? "Active" : "Inactive"}
                                      </span>
                                    </div>
                                  ) : (
                                    <span
                                      className={`status-badge ${
                                        isActive
                                          ? "status-active"
                                          : "status-inactive"
                                      }`}
                                    >
                                      <span className="status-dot" />
                                      {isActive ? "Active" : "Inactive"}
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* =================================================
                  HOSTEL DETAILS
              ================================================= */}
                              <div className="hostel-card-details">
                                {/* Address */}
                                <div className="hostel-detail-row">
                                  <div className="hostel-detail-icon">
                                    <i className="ri-map-pin-line" />
                                  </div>

                                  <div className="hostel-detail-content">
                                    <span className="hostel-detail-label">
                                      Address
                                    </span>

                                    <span
                                      className="hostel-detail-value hostel-address"
                                      title={hostel?.address}
                                    >
                                      {hostel?.address ||
                                        "Address not available"}
                                    </span>
                                  </div>
                                </div>

                                {/* Contact */}
                                <div className="hostel-detail-row">
                                  <div className="hostel-detail-icon">
                                    <i className="ri-phone-line" />
                                  </div>

                                  <div className="hostel-detail-content">
                                    <span className="hostel-detail-label">
                                      Contact
                                    </span>

                                    {hostel?.mobile ? (
                                      <div className="hostel-phone-list">
                                        {String(hostel.mobile)
                                          .split(",")
                                          .map((number, index) => (
                                            <a
                                              key={index}
                                              href={`tel:${number.trim()}`}
                                              className="hostel-phone"
                                            >
                                              <i className="ri-phone-line" />
                                              {number.trim()}
                                            </a>
                                          ))}
                                      </div>
                                    ) : (
                                      <span className="hostel-detail-value text-muted">
                                        Not available
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* =================================================
    HOSTEL OVERVIEW
================================================= */}
                              <div className="hostel-card-overview">
                                <div className="hostel-overview-item">
                                  <strong>{hostel?.students_count || 0}</strong>
                                  <span>Students</span>
                                </div>

                                <div className="hostel-overview-item">
                                  <strong>
                                    {hostel?.active_students_count || 0}
                                  </strong>
                                  <span>Active</span>
                                </div>

                                <div className="hostel-overview-item">
                                  <strong>
                                    {hostel?.inactive_students_count || 0}
                                  </strong>
                                  <span>Inactive</span>
                                </div>

                                <div className="hostel-overview-item">
                                  <strong>{hostel?.rooms_count || 0}</strong>
                                  <span>Rooms</span>
                                </div>
                              </div>

                              {/* =================================================
    ACTIONS
================================================= */}
                              <div className="hostel-card-footer">
                                <div className="hostel-actions">
                                  {isSuperAdmin && (
                                    <button
                                      type="button"
                                      className="hostel-action-icon action-assign"
                                      title="Assign Staff"
                                      aria-label="Assign Staff"
                                      data-bs-toggle="modal"
                                      data-bs-target="#assignStaffToHostel"
                                      onClick={() => {
                                        setSelectedHostel(hostel);
                                        getStaffList();
                                      }}
                                    >
                                      <i className="ri-user-add-line" />
                                    </button>
                                  )}

                                  {hasAccess("/hostel/edit") && (
                                    <button
                                      type="button"
                                      className="hostel-action-icon action-edit"
                                      title="Edit Hostel"
                                      aria-label="Edit Hostel"
                                      data-bs-toggle="modal"
                                      data-bs-target="#editHostel"
                                      onClick={() => setModalData(hostel)}
                                    >
                                      <i className="ri-edit-line" />
                                    </button>
                                  )}

                                  {hasAccess("/hostel/delete") && (
                                    <button
                                      type="button"
                                      className="hostel-action-icon action-delete"
                                      title="Delete Hostel"
                                      aria-label="Delete Hostel"
                                      onClick={() => confirmDelete(hostel)}
                                    >
                                      <i className="ri-delete-bin-line" />
                                    </button>
                                  )}
                                </div>

                                {hasAccess("/hostel") && (
                                  <Link
                                    to="/hostel"
                                    className="hostel-view-btn"
                                    onClick={() => {
                                      localStorage.setItem(
                                        "hostel",
                                        JSON.stringify(hostel),
                                      );

                                      localStorage.setItem(
                                        "hostel_id",
                                        hostel?.id,
                                      );
                                    }}
                                  >
                                    View Details
                                    <i className="ri-arrow-right-line" />
                                  </Link>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      /* =========================================================
         EMPTY STATE
      ========================================================= */
                      <div className="hostel-empty-state">
                        <div className="empty-state-icon">
                          <i className="ri-building-2-line" />
                        </div>

                        <h5 className="mt-3 mb-1">No hostels found</h5>

                        <p className="text-muted mb-3">
                          There are no hostels available on this page.
                        </p>

                        {hasAccess("/hostel/add") && (
                          <button
                            type="button"
                            className="btn btn-primary btn-sm px-4"
                            data-bs-toggle="modal"
                            data-bs-target="#addNewHostel"
                          >
                            <i className="ri-add-line me-1" />
                            Add First Hostel
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* =========================================================
      PAGINATION
  ========================================================= */}
                  {hostels?.total > 0 && (
                    <div className="card-footer border-top px-4 py-3">
                      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
                        <div className="text-muted small">
                          Showing{" "}
                          <strong className="text-body">
                            {hostels?.from || 0}
                          </strong>{" "}
                          to{" "}
                          <strong className="text-body">
                            {hostels?.to || 0}
                          </strong>{" "}
                          of{" "}
                          <strong className="text-body">
                            {hostels?.total || 0}
                          </strong>{" "}
                          hostels
                        </div>

                        {hostels?.links?.length > 1 && (
                          <nav aria-label="Hostel pagination">
                            <ul className="pagination pagination-sm mb-0">
                              {hostels.links.map((btn, i) => {
                                const isPrevious = i === 0;
                                const isNext = i === hostels.links.length - 1;

                                return (
                                  <li
                                    key={`${btn?.label}-${i}`}
                                    className={`page-item ${
                                      btn?.active ? "active" : ""
                                    } ${!btn?.url ? "disabled" : ""}`}
                                  >
                                    <button
                                      type="button"
                                      className="page-link"
                                      disabled={!btn?.url}
                                      onClick={() => handlePageChange(btn, i)}
                                    >
                                      {isPrevious ? (
                                        <i className="ri-arrow-left-s-line" />
                                      ) : isNext ? (
                                        <i className="ri-arrow-right-s-line" />
                                      ) : (
                                        btn?.label
                                      )}
                                    </button>
                                  </li>
                                );
                              })}
                            </ul>
                          </nav>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* =========================================================
          STUDENTS MODAL
      ========================================================= */}
      <StudentsModal type={studentType} />

      {/* =========================================================
          ADD HOSTEL MODAL
      ========================================================= */}
      <div
        className="modal fade"
        id="addNewHostel"
        tabIndex={-1}
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div className="modal-content border-0 shadow">
            <form onSubmit={createHostel}>
              <div className="modal-header border-bottom px-4 py-3">
                <div>
                  <h5 className="modal-title fw-bold mb-1">Add New Hostel</h5>

                  <small className="text-muted">
                    Enter the hostel details below
                  </small>
                </div>

                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                />
              </div>

              <div className="modal-body px-4 py-4">
                <div className="mb-4">
                  <label className="form-label fw-semibold">Hostel Name</label>

                  <div className="input-group input-group-merge">
                    <span className="input-group-text">
                      <i className="ri-building-line" />
                    </span>

                    <input
                      type="text"
                      className="form-control input-uppercase"
                      placeholder="Enter hostel name"
                      name="name"
                      required
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="form-label fw-semibold">
                    Contact Number
                  </label>

                  <div className="input-group input-group-merge">
                    <span className="input-group-text">
                      <i className="ri-phone-line" />
                    </span>

                    <input
                      type="tel"
                      className="form-control"
                      placeholder="Enter contact number"
                      name="mobile"
                    />
                  </div>
                </div>

                <div>
                  <label className="form-label fw-semibold">Address</label>

                  <div className="input-group input-group-merge">
                    <span className="input-group-text align-items-start pt-3">
                      <i className="ri-map-pin-line" />
                    </span>

                    <textarea
                      className="form-control input-uppercase"
                      rows="3"
                      placeholder="Enter hostel address"
                      name="address"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="modal-footer px-4 py-3">
                <button
                  type="button"
                  className="btn btn-label-secondary"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary px-4"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      />
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line me-1" />
                      Save Hostel
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* =========================================================
          EDIT HOSTEL MODAL
      ========================================================= */}
      <div
        className="modal fade"
        id="editHostel"
        tabIndex={-1}
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content border-0 shadow">
            <div className="modal-header border-bottom px-4 py-3">
              <div className="d-flex align-items-center gap-3">
                <div className="avatar">
                  <span className="avatar-initial rounded bg-label-primary">
                    <i className="ri-edit-line ri-20px" />
                  </span>
                </div>

                <div>
                  <h5 className="modal-title fw-bold mb-1">Edit Hostel</h5>

                  <small className="text-muted">
                    Update hostel information
                  </small>
                </div>
              </div>

              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>

            <form onSubmit={updateHostel}>
              <div className="modal-body px-4 py-4">
                <input type="hidden" name="id" value={modalData?.id || ""} />

                <div className="row g-4">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">
                      Hostel Name
                    </label>

                    <div className="input-group input-group-merge">
                      <span className="input-group-text">
                        <i className="ri-building-line" />
                      </span>

                      <input
                        type="text"
                        className="form-control input-uppercase"
                        placeholder="Enter hostel name"
                        name="name"
                        defaultValue={modalData?.name || ""}
                        required
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">
                      Contact Number
                    </label>

                    <div className="input-group input-group-merge">
                      <span className="input-group-text">
                        <i className="ri-phone-line" />
                      </span>

                      <input
                        type="tel"
                        className="form-control"
                        placeholder="Enter contact number"
                        name="mobile"
                        defaultValue={modalData?.mobile || ""}
                      />
                    </div>
                  </div>

                  <div className="col-12">
                    <label className="form-label fw-semibold">Address</label>

                    <div className="input-group input-group-merge">
                      <span className="input-group-text align-items-start pt-3">
                        <i className="ri-map-pin-line" />
                      </span>

                      <textarea
                        className="form-control input-uppercase"
                        rows="4"
                        placeholder="Enter hostel address"
                        name="address"
                        defaultValue={modalData?.address || ""}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer px-4 py-3">
                <button
                  type="button"
                  className="btn btn-label-secondary"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="btn btn-primary px-4"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      />
                      Updating...
                    </>
                  ) : (
                    <>
                      <i className="ri-save-3-line me-1" />
                      Update Hostel
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* =========================================================
    ASSIGN STAFF TO HOSTEL MODAL
========================================================= */}
      {isSuperAdmin && (
        <div
          className="modal fade"
          id="assignStaffToHostel"
          tabIndex={-1}
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered modal-md">
            <div className="modal-content border-0 shadow">
              <form onSubmit={assignStaffToHostel}>
                {/* Header */}
                <div className="modal-header border-bottom px-4 py-3">
                  <div className="d-flex align-items-center gap-3">
                    <div className="avatar">
                      <span className="avatar-initial rounded bg-label-primary">
                        <i className="ri-user-add-line ri-20px" />
                      </span>
                    </div>

                    <div>
                      <h5 className="modal-title fw-bold mb-1">
                        Assign Staff Members
                      </h5>

                      <small className="text-muted">
                        Assign one or more staff members to this hostel
                      </small>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  />
                </div>

                {/* Body */}
                <div className="modal-body px-4 py-4">
                  {/* Hostel */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold">Hostel</label>

                    <div className="d-flex align-items-center gap-3 p-3 rounded border">
                      <div className="avatar avatar-sm">
                        <span className="avatar-initial rounded bg-label-primary">
                          <i className="ri-building-line" />
                        </span>
                      </div>

                      <div>
                        <div className="fw-semibold">
                          {selectedHostel?.name || "—"}
                        </div>

                        {selectedHostel?.address && (
                          <small className="text-muted">
                            {selectedHostel.address}
                          </small>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Staff */}
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <label className="form-label fw-semibold mb-0">
                        Staff Members
                      </label>

                      {selectedStaffIds.length > 0 && (
                        <span className="badge bg-label-primary">
                          {selectedStaffIds.length} selected
                        </span>
                      )}
                    </div>

                    <div className="staff-selection-list">
                      {staffList.length > 0 ? (
                        staffList.map((staff) => {
                          const staffId = String(staff?.id);

                          const isSelected = selectedStaffIds.includes(staffId);

                          return (
                            <label
                              key={staff?.id}
                              className={`staff-selection-item ${
                                isSelected ? "selected" : ""
                              }`}
                            >
                              <input
                                type="checkbox"
                                className="form-check-input mt-0"
                                checked={isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedStaffIds((prev) => [
                                      ...prev,
                                      staffId,
                                    ]);
                                  } else {
                                    setSelectedStaffIds((prev) =>
                                      prev.filter((id) => id !== staffId),
                                    );
                                  }
                                }}
                              />

                              <div className="staff-selection-info">
                                <div className="staff-selection-name">
                                  {staff?.name || "Unnamed Staff"}
                                </div>

                                {staff?.role_info?.name && (
                                  <div className="staff-selection-role">
                                    {staff.role_info.name}
                                  </div>
                                )}
                              </div>
                            </label>
                          );
                        })
                      ) : (
                        <div className="text-center text-muted py-4">
                          <i className="ri-user-search-line ri-24px d-block mb-2" />
                          No staff available
                        </div>
                      )}
                    </div>

                    <small className="text-muted mt-2 d-block">
                      Select one or more staff members to assign them to this
                      hostel.
                    </small>
                  </div>
                </div>

                {/* Footer */}
                <div className="modal-footer px-4 py-3">
                  <button
                    type="button"
                    className="btn btn-label-secondary"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="btn btn-primary px-4"
                    disabled={
                      loading || !staffList.length || !selectedStaffIds.length
                    }
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        />
                        Assigning...
                      </>
                    ) : (
                      <>
                        <i className="ri-user-add-line me-1" />
                        Assign{" "}
                        {selectedStaffIds.length > 0
                          ? `${selectedStaffIds.length} Staff`
                          : "Staff"}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================
          PAGE SPECIFIC STYLES
      ========================================================= */}
      <style>{`
  /* =========================================================
     HOSTEL MANAGEMENT - CLEAN CARD UI
  ========================================================= */

  .hostel-management-card {
    background: var(--bs-card-bg, var(--bs-body-bg));
    color: var(--bs-body-color);
    border-color: var(--bs-border-color);
  }

  /* =========================================================
     TOP STAT CARDS
  ========================================================= */

  .hostel-stat-card {
    position: relative;
    display: flex;
    align-items: center;
    gap: 13px;
    min-width: 0;
    padding: 16px;

    background: var(--bs-card-bg, var(--bs-body-bg));
    color: var(--bs-body-color);

    border: 1px solid var(--bs-border-color);
    border-radius: 12px;

    box-shadow: var(--bs-box-shadow-sm, 0 2px 8px rgba(0,0,0,.05));

    transition:
      transform .18s ease,
      box-shadow .18s ease,
      border-color .18s ease;
  }

  .hostel-stat-card:hover {
    transform: translateY(-2px);
    box-shadow: var(--bs-box-shadow, 0 5px 16px rgba(0,0,0,.08));
  }

  .hostel-stat-card.clickable {
    cursor: pointer;
  }

  .stat-icon {
    width: 42px;
    height: 42px;
    min-width: 42px;

    display: flex;
    align-items: center;
    justify-content: center;

    border-radius: 10px;
    font-size: 20px;
  }

  .stat-content {
    min-width: 0;
    flex: 1;
  }

  .stat-content span {
    display: block;
    max-width: 100%;

    color: var(--bs-secondary-color);

    font-size: 11px;
    font-weight: 600;
    line-height: 1.3;

    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    margin-bottom: 3px;
  }

  .stat-content h3 {
    margin: 0;
    font-size: 23px;
    font-weight: 700;
    line-height: 1.15;
    color: var(--bs-body-color);
  }

  .stat-arrow {
    position: absolute;
    top: 9px;
    right: 10px;

    font-size: 14px;
    color: var(--bs-secondary-color);

    opacity: 0;
    transition: opacity .18s ease;
  }

  .hostel-stat-card:hover .stat-arrow {
    opacity: .65;
  }

  .stat-warning { border-top: 3px solid var(--bs-warning); }
  .stat-warning .stat-icon {
    color: var(--bs-warning);
    background: rgba(var(--bs-warning-rgb), .12);
  }
  .stat-warning h3 { color: var(--bs-warning); }

  .stat-primary { border-top: 3px solid var(--bs-primary); }
  .stat-primary .stat-icon {
    color: var(--bs-primary);
    background: rgba(var(--bs-primary-rgb), .12);
  }
  .stat-primary h3 { color: var(--bs-primary); }

  .stat-success { border-top: 3px solid var(--bs-success); }
  .stat-success .stat-icon {
    color: var(--bs-success);
    background: rgba(var(--bs-success-rgb), .12);
  }
  .stat-success h3 { color: var(--bs-success); }

  .stat-danger { border-top: 3px solid var(--bs-danger); }
  .stat-danger .stat-icon {
    color: var(--bs-danger);
    background: rgba(var(--bs-danger-rgb), .12);
  }
  .stat-danger h3 { color: var(--bs-danger); }

  .stat-info { border-top: 3px solid var(--bs-info); }
  .stat-info .stat-icon {
    color: var(--bs-info);
    background: rgba(var(--bs-info-rgb), .12);
  }
  .stat-info h3 { color: var(--bs-info); }

  .stat-purple { border-top: 3px solid var(--bs-primary); }
  .stat-purple .stat-icon {
    color: var(--bs-primary);
    background: rgba(var(--bs-primary-rgb), .12);
  }
  .stat-purple h3 { color: var(--bs-primary); }


  /* =========================================================
     HOSTEL LIST HEADER
  ========================================================= */

  .hostel-section-icon {
    width: 38px;
    height: 38px;
    min-width: 38px;

    display: flex;
    align-items: center;
    justify-content: center;

    border-radius: 9px;

    color: var(--bs-primary);
    background: rgba(var(--bs-primary-rgb), .10);

    font-size: 18px;
  }

  .hostel-count-label {
    display: inline-flex;
    align-items: center;

    padding: 7px 11px;

    border-radius: 8px;

    color: var(--bs-secondary-color);
    background: var(--bs-tertiary-bg, var(--bs-secondary-bg));

    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
  }


  /* =========================================================
     HOSTEL GRID
  ========================================================= */

  .hostel-grid-container {
    padding: 20px;
  }

  .hostel-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
  }


  /* =========================================================
     HOSTEL CARD
  ========================================================= */

  .hostel-card {
    min-width: 0;
    min-height: 0;

    display: flex;
    flex-direction: column;

    background: var(--bs-card-bg, var(--bs-body-bg));
    color: var(--bs-body-color);

    border: 1px solid var(--bs-border-color);
    border-radius: 12px;

    overflow: hidden;

    transition:
      transform .18s ease,
      border-color .18s ease,
      box-shadow .18s ease;
  }

  .hostel-card:hover {
    transform: translateY(-2px);
    border-color: rgba(var(--bs-primary-rgb), .32);
    box-shadow: 0 7px 20px rgba(0,0,0,.06);
  }


  /* =========================================================
     CARD HEADER
  ========================================================= */

  .hostel-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    gap: 12px;

    padding: 16px 17px;

    border-bottom: 1px solid var(--bs-border-color);
  }

  .hostel-card-header > .d-flex {
    min-width: 0;
  }

  .hostel-avatar {
    width: 42px;
    height: 42px;
    min-width: 42px;

    display: flex;
    align-items: center;
    justify-content: center;

    border-radius: 10px;

    color: var(--bs-primary);
    background: rgba(var(--bs-primary-rgb), .10);

    font-size: 20px;
  }

  .hostel-card-title {
    min-width: 0;
  }

  .hostel-name {
    display: block;

    max-width: 185px;

    font-size: 14px;
    line-height: 1.25;

    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .hostel-number {
    display: block;

    margin-top: 3px;

    color: var(--bs-secondary-color);

    font-size: 10px;
    font-weight: 500;
  }

  .hostel-status-wrapper {
    flex-shrink: 0;
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;

    padding: 5px 8px;

    border-radius: 20px;

    font-size: 10px;
    font-weight: 700;

    white-space: nowrap;
  }

  .status-active {
    color: var(--bs-success);
    background: rgba(var(--bs-success-rgb), .11);
  }

  .status-inactive {
    color: var(--bs-danger);
    background: rgba(var(--bs-danger-rgb), .11);
  }

  .status-dot {
    width: 6px;
    height: 6px;
    min-width: 6px;

    border-radius: 50%;
    background: currentColor;
  }


  /* =========================================================
     HOSTEL DETAILS
  ========================================================= */

  .hostel-card-details {
    padding: 14px 17px 13px;
  }

  .hostel-detail-row {
    display: flex;
    align-items: flex-start;

    gap: 10px;

    margin-bottom: 12px;
  }

  .hostel-detail-row:last-child {
    margin-bottom: 0;
  }

  .hostel-detail-icon {
    width: 28px;
    height: 28px;
    min-width: 28px;

    display: flex;
    align-items: center;
    justify-content: center;

    border-radius: 7px;

    color: var(--bs-secondary-color);
    background: var(--bs-tertiary-bg, var(--bs-secondary-bg));

    font-size: 14px;
  }

  .hostel-detail-content {
    min-width: 0;
    flex: 1;

    display: flex;
    flex-direction: column;
  }

  .hostel-detail-label {
    color: var(--bs-secondary-color);

    font-size: 9px;
    font-weight: 700;
    line-height: 1.2;

    letter-spacing: .45px;
    text-transform: uppercase;

    margin-bottom: 3px;
  }

  .hostel-detail-value {
    display: block;

    color: var(--bs-body-color);

    font-size: 11.5px;
    font-weight: 500;
    line-height: 1.45;
  }

  .hostel-address {
    display: -webkit-box;
    max-width: 100%;

    overflow: hidden;
    text-overflow: ellipsis;

    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .hostel-phone-list {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 10px;

    min-width: 0;
  }

  .hostel-phone {
    display: inline-flex;
    align-items: center;
    gap: 4px;

    color: var(--bs-body-color);
    text-decoration: none;

    font-size: 11.5px;
    font-weight: 500;

    white-space: nowrap;
  }

  .hostel-phone i {
    color: var(--bs-primary);
    font-size: 12px;
  }

  .hostel-phone:hover {
    color: var(--bs-primary);
  }


  /* =========================================================
     HOSTEL OVERVIEW
     One information strip - no mini cards or icon boxes.
  ========================================================= */

  .hostel-card-overview {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));

    margin: 0 17px;

    border-top: 1px solid var(--bs-border-color);
    border-bottom: 1px solid var(--bs-border-color);
  }

  .hostel-overview-item {
    min-width: 0;

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    padding: 11px 5px;
  }

  .hostel-overview-item + .hostel-overview-item {
    border-left: 1px solid var(--bs-border-color);
  }

  .hostel-overview-item strong {
    display: block;

    color: var(--bs-body-color);

    font-size: 17px;
    font-weight: 700;
    line-height: 1.1;
  }

  .hostel-overview-item span {
    display: block;

    margin-top: 3px;

    color: var(--bs-secondary-color);

    font-size: 9px;
    font-weight: 600;
    line-height: 1.1;

    white-space: nowrap;
  }

  .hostel-overview-item:nth-child(1) strong {
    color: var(--bs-primary);
  }

  .hostel-overview-item:nth-child(2) strong {
    color: var(--bs-success);
  }

  .hostel-overview-item:nth-child(3) strong {
    color: var(--bs-danger);
  }

  .hostel-overview-item:nth-child(4) strong {
    color: var(--bs-info);
  }


  /* =========================================================
     CARD FOOTER
  ========================================================= */

  .hostel-card-footer {
    margin-top: auto;

    min-height: 52px;

    display: flex;
    align-items: center;
    justify-content: space-between;

    gap: 10px;

    padding: 9px 17px;

    background: var(--bs-tertiary-bg, var(--bs-secondary-bg));
  }

  .hostel-actions {
    display: flex;
    align-items: center;
    gap: 3px;
  }

  .hostel-action-icon {
    width: 30px;
    height: 30px;
    min-width: 30px;

    display: inline-flex;
    align-items: center;
    justify-content: center;

    padding: 0;

    border: 0;
    border-radius: 7px;

    background: transparent;

    font-size: 15px;

    transition:
      background-color .15s ease,
      transform .15s ease;
  }

  .hostel-action-icon:hover {
    transform: translateY(-1px);
  }

  .action-assign {
    color: var(--bs-primary);
  }

  .action-assign:hover {
    color: var(--bs-primary);
    background: rgba(var(--bs-primary-rgb), .11);
  }

  .action-edit {
    color: var(--bs-info);
  }

  .action-edit:hover {
    color: var(--bs-info);
    background: rgba(var(--bs-info-rgb), .11);
  }

  .action-delete {
    color: var(--bs-danger);
  }

  .action-delete:hover {
    color: var(--bs-danger);
    background: rgba(var(--bs-danger-rgb), .11);
  }

  .hostel-view-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;

    padding: 7px 11px;

    border-radius: 7px;

    color: var(--bs-primary);
    background: rgba(var(--bs-primary-rgb), .09);

    text-decoration: none;

    font-size: 10.5px;
    font-weight: 700;

    white-space: nowrap;

    transition:
      background-color .15s ease,
      transform .15s ease;
  }

  .hostel-view-btn:hover {
    color: var(--bs-primary);
    background: rgba(var(--bs-primary-rgb), .15);
    transform: translateX(1px);
  }

  .hostel-view-btn i {
    font-size: 13px;
  }


  /* =========================================================
     EMPTY STATE
  ========================================================= */

  .hostel-empty-state {
    padding: 55px 20px;
    text-align: center;
  }

  .empty-state-icon {
    width: 62px;
    height: 62px;

    margin: 0 auto;

    display: flex;
    align-items: center;
    justify-content: center;

    border-radius: 50%;

    color: var(--bs-primary);
    background: rgba(var(--bs-primary-rgb), .10);

    font-size: 27px;
  }


  /* =========================================================
     STAFF SELECTION
  ========================================================= */

  .staff-selection-list {
    max-height: 280px;
    overflow-y: auto;

    border: 1px solid var(--bs-border-color);
    border-radius: 10px;

    background: var(--bs-body-bg);
  }

  .staff-selection-item {
    display: flex;
    align-items: center;
    gap: 12px;

    padding: 11px 13px;
    margin: 0;

    cursor: pointer;

    border-bottom: 1px solid var(--bs-border-color);

    transition: background-color .15s ease;
  }

  .staff-selection-item:last-child {
    border-bottom: 0;
  }

  .staff-selection-item:hover {
    background: rgba(var(--bs-primary-rgb), .05);
  }

  .staff-selection-item.selected {
    background: rgba(var(--bs-primary-rgb), .08);
  }

  .staff-selection-item .form-check-input {
    flex-shrink: 0;
    cursor: pointer;
  }

  .staff-selection-info {
    min-width: 0;
    flex: 1;
  }

  .staff-selection-name {
    color: var(--bs-body-color);
    font-size: 13px;
    font-weight: 600;
    line-height: 1.3;
  }

  .staff-selection-role {
    margin-top: 2px;
    color: var(--bs-secondary-color);
    font-size: 11px;
  }


  /* =========================================================
     MODALS
  ========================================================= */

  .modal-content {
    background: var(--bs-modal-bg, var(--bs-body-bg));
    color: var(--bs-body-color);

    border-color: var(--bs-border-color);
    border-radius: 12px;
    overflow: hidden;
  }

  .modal-header {
    border-bottom-color: var(--bs-border-color);
  }

  .modal-footer {
    background: var(--bs-tertiary-bg, var(--bs-secondary-bg));
    border-top-color: var(--bs-border-color);
  }


  /* =========================================================
     FORM INPUTS
  ========================================================= */

  .input-group-merge .input-group-text {
    background: var(--bs-body-bg);
    color: var(--bs-secondary-color);
    border-color: var(--bs-border-color);
  }

  .input-group-merge .form-control {
    background: var(--bs-body-bg);
    color: var(--bs-body-color);
    border-left: 0;
  }

  .input-group-merge .form-control::placeholder {
    color: var(--bs-secondary-color);
    opacity: .7;
  }

  .input-group-merge:focus-within .input-group-text,
  .input-group-merge:focus-within .form-control {
    border-color: var(--bs-primary);
  }


  /* =========================================================
     RESPONSIVE
  ========================================================= */

  @media (max-width: 1199px) {
    .hostel-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .hostel-name {
      max-width: 220px;
    }
  }

  @media (max-width: 767px) {
    .hostel-grid-container {
      padding: 15px;
    }

    .hostel-grid {
      grid-template-columns: 1fr;
      gap: 14px;
    }

    .hostel-card-header {
      padding: 15px;
    }

    .hostel-card-details {
      padding: 13px 15px;
    }

    .hostel-card-overview {
      margin: 0 15px;
    }

    .hostel-card-footer {
      padding: 9px 15px;
    }

    .stat-content h3 {
      font-size: 21px;
    }
  }

  @media (max-width: 430px) {
    .hostel-card-header {
      align-items: flex-start;
    }

    .hostel-avatar {
      width: 39px;
      height: 39px;
      min-width: 39px;
      font-size: 18px;
    }

    .hostel-name {
      max-width: 145px;
      font-size: 13.5px;
    }

    .hostel-card-overview {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .hostel-overview-item:nth-child(3) {
      border-left: 0;
      border-top: 1px solid var(--bs-border-color);
    }

    .hostel-overview-item:nth-child(4) {
      border-top: 1px solid var(--bs-border-color);
    }

    .hostel-overview-item {
      padding: 10px 5px;
    }

    .hostel-view-btn {
      padding: 7px 10px;
    }

    .hostel-view-btn i {
      font-size: 15px;
    }
  }
      `}</style>
    </>
  );
}
